import { useContext, useMemo, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import {
  Anchor,
  Box,
  Icon,
  IconButton,
  Menu,
  Table,
  Tag,
  Text,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconCircleFilled, IconMoreFilled, IconWarningFilled } from '@zillow/constellation-icons';
import {
  AccountViewListingStatus,
  CometUserRoleTypeString,
  useRentalManagerAccountViewZrmWebApiHandler_inquiries,
  useRentalManagerAccountViewZrmWebApiHandler_leases,
  useRentalManagerAccountViewZrmWebApiHandler_payments,
  useRentalManagerUserPropertyEditWebApiHandler_isApplicationsAccepted,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/rental-manager-env';

import { AppSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import PropertiesActions from '../../../actions/PropertiesActions';

import { RMWContext } from '../../../providers/RMWContext';
import stringHelper from '../../../utils/stringHelper';
import propertyUtils from '../../../utils/propertyUtils';
import { RMW_SUB_PATH } from '../../../pathsManifest';
import { escapeRoute, PANEL_ID } from '../../../utils/escapeRoute';
import PropertyConstants from '../../../constants/PropertyConstants';

import PropertyPanel from '../../../model/PropertyPanel';

import PropertiesPageCSEvents from '../../../model/ClickstreamEvent/PropertiesPageCSEvents';
import ManageTenantsCSEvents from '../../../model/ClickstreamEvent/ManageTenantsCSEvents';

import EnableApplicationsModal from '../../Modal/EnableApplicationsModal';

import MediumIllustrationHouse2x from '../../../images/MediumIllustrationHouse@2x.png';
import {
  CELL_DIMENSIONS,
  cellAnchorStyles,
  flexContainerStyles,
  propertyAddressStyles,
  propertyPhotoContainerStyles,
  propertyPhotoRecipe,
  TableCell,
  tagStyles,
} from './styles';

import type React from 'react';
import type { TagPropsInterface } from '@zillow/constellation-10';
import type {
  AccountViewTableCellCtaDto,
  AccountViewTableCellDto,
  WebAnalyticsLeaseDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';
import type { PanelProps } from '../../../model/PropertyPanel';
import type { PropertyProps } from '../../../model/Property';

type PropertyRowProps = {
  index?: number | null;
  listing: PropertyProps;
  showMoreListingAlias: string;
  setShowMoreListingAlias: (listingAlias: string) => void;
  onUpsellCellHover: (
    columnLabel: string,
    currentProperty: ZrmClickstreamListing,
    lease?: WebAnalyticsLeaseDto,
  ) => void;
};

const { PROPERTY_TABLE_COLUMNS, PROPERTY_TABLE_TRIGGER_OBJECT_MAP } = PropertyConstants;

const PropertyRow = ({
  index = null,
  listing,
  showMoreListingAlias,
  setShowMoreListingAlias,
  onUpsellCellHover,
}: PropertyRowProps) => {
  const { paths } = useContext(RMWContext);
  const dispatch = useDispatch();
  const { enqueueToast } = useToast();
  const [displayApplicationsModal, setDisplayApplicationsModal] = useState(false);

  const cellWidth = CELL_DIMENSIONS.WIDTH;
  const textMaxWidth = {
    base: `${cellWidth + 100}px`,
    lg: `${cellWidth}px`,
    xxl: `${cellWidth + 50}px`,
  };

  const { canAccessDashboardRedesign, isPostingPathAdmin, user, isScreenMobile, isScreenLgGTE, isScreenXlGTE } =
    useSelector((state) => {
      return {
        canAccessDashboardRedesign: state.app.canAccessDashboardRedesign,
        isPostingPathAdmin: state.user.isPostingPathAdmin,
        user: state.user,
        isScreenMobile: AppSelectors.makeIsScreenMobile(state),
        isScreenLgGTE: AppSelectors.makeIsScreenLgGTE(state),
        isScreenXlGTE: AppSelectors.makeIsScreenXlGTE(state),
      };
    }, shallowEqual);

  const {
    photoUrls: { thumb },
    status: { text: listingStatusText, tagAppearance: listingStatusTagAppearance, status: listingStatus },
    address,
    listingAlias,
    dateLastActivatedTimestamp,
    isMultifamily,
    name,
    panels,
    userManagementCompanyId,
  } = listing;

  const { street, unit } = address;
  const fullAddress = unit ? `${street} #${unit}` : street || name;
  const shouldHideColumnContent =
    listingStatus === AccountViewListingStatus.ARCHIVED || listingStatus === AccountViewListingStatus.UNABLE_TO_PUBLISH;

  const displayManagePermissions =
    user.roles.includes(CometUserRoleTypeString.PROPERTY_MANAGER_COMPANY_ADMIN) &&
    user.userManagementCompanyId === userManagementCompanyId;

  const { data: paymentsData } = useRentalManagerAccountViewZrmWebApiHandler_payments(
    { listingAlias },
    {
      enabled: !shouldHideColumnContent,
    },
  );
  const { data: inquiriesData, refetch: refetchInquiries } = useRentalManagerAccountViewZrmWebApiHandler_inquiries(
    { listingAlias },
    {
      enabled: !shouldHideColumnContent,
    },
  );
  const { data: leasesData } = useRentalManagerAccountViewZrmWebApiHandler_leases(
    { listingAlias },
    {
      enabled: !shouldHideColumnContent,
    },
  );

  const { data: { lease, ...webAnalyticsData } = {} } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get({
    listingAlias,
    includeLeases: true,
  });

  const { mutateAsync: setIsApplicationsAccepted } =
    useRentalManagerUserPropertyEditWebApiHandler_isApplicationsAccepted();

  const panelsToRender = useMemo(() => {
    const panelsToAdd: Array<PanelProps | null> = canAccessDashboardRedesign
      ? [
          PropertyPanel({
            id: dateLastActivatedTimestamp ? PANEL_ID.EDIT_LISTING : PANEL_ID.LIST,
            title: dateLastActivatedTimestamp ? 'Edit listing' : 'List',
            urlPath: `${paths.propertiesPage}/${listingAlias}/${
              dateLastActivatedTimestamp || isMultifamily ? 'listing' : 'create-listing'
            }`,
          }),
          isMultifamily
            ? null
            : PropertyPanel({
                id: PANEL_ID.OVERVIEW,
                title: 'Overview',
                urlPath: `${paths.propertiesPage}/${listingAlias}/${RMW_SUB_PATH.DASHBOARD}/overview`,
              }),
          displayManagePermissions
            ? PropertyPanel({
                id: PANEL_ID.MANAGE_PERMISSIONS,
                title: 'Manage Permissions',
                urlPath: `${paths.userManagementPage}/listing-permissions/${listingAlias}`,
              })
            : null,
        ]
      : [];

    return [...panelsToAdd, ...panels].filter((item) => item && !item.isDisabled);
  }, [panels]);

  const targetListingRoute = useMemo(() => {
    if (isMultifamily) {
      return `${paths.propertiesPage}/${listingAlias}/listing`;
    }

    const panelToRender = panelsToRender[canAccessDashboardRedesign ? 1 : 0];
    return panelToRender ? panelToRender.urlPath : '';
  }, [canAccessDashboardRedesign, isMultifamily, listingAlias]);

  const routerHandler = (pathToRoute: string, openNewTab = false) => {
    if (openNewTab) {
      window.router.updateLocation(pathToRoute, '_blank');
    } else {
      window.router.transitionTo(pathToRoute);
    }
  };

  const handleRowClick = (event: React.MouseEvent<HTMLElement>) => {
    const openNewTab = event.ctrlKey || event.metaKey;
    event.preventDefault();
    event.stopPropagation();

    PropertiesPageCSEvents.PropertyCardClick({ user });

    dispatch(PropertiesActions.updateCurrentProperty({ ...address }));

    if (targetListingRoute) {
      routerHandler(targetListingRoute, openNewTab);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    PropertiesPageCSEvents.PropertyCardOverflowMenu({
      user,
      currentProperty: webAnalyticsData,
      lease,
    });
  };

  const handleMenuItemClick = ({ urlPath: url = '', id = '' }: PanelProps, event: React.MouseEvent<HTMLElement>) => {
    if (id === PANEL_ID.OVERVIEW) {
      dispatch(PropertiesActions.updateCurrentProperty({ ...address }));
    }

    const openNewTab = event.ctrlKey || event.metaKey;

    if (escapeRoute(id)) {
      if (openNewTab) {
        window.router.updateLocation(url);
      } else {
        window.router.updateLocation(url, '_blank');
      }
    } else {
      routerHandler(url, openNewTab);
    }
  };

  const handleCellCtaClick = (
    event: React.MouseEvent<HTMLElement>,
    cta: Partial<AccountViewTableCellCtaDto>,
    label: string,
  ) => {
    event.stopPropagation();

    if (cta.text === 'Enable applications') {
      event.preventDefault();
      setDisplayApplicationsModal(true);
    }

    switch (label) {
      case PROPERTY_TABLE_COLUMNS.RENT:
        PropertiesPageCSEvents.PropertyCardPayments({
          user,
          currentProperty: webAnalyticsData,
          lease,
          triggerObject:
            PROPERTY_TABLE_TRIGGER_OBJECT_MAP.RENT_TRIGGERS[
              cta.text as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.RENT_TRIGGERS
            ] || cta.text,
        });
        break;
      case PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS:
        PropertiesPageCSEvents.PropertyCardPotentialTenants({
          user,
          currentProperty: webAnalyticsData,
          lease,
          triggerObject:
            PROPERTY_TABLE_TRIGGER_OBJECT_MAP.POTENTIAL_TENANTS_TRIGGERS[
              cta.text as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.POTENTIAL_TENANTS_TRIGGERS
            ] || cta.text,
        });
        break;
      case PROPERTY_TABLE_COLUMNS.LEASES:
        PropertiesPageCSEvents.PropertyCardLeases({
          user,
          currentProperty: webAnalyticsData,
          lease,
          triggerObject:
            PROPERTY_TABLE_TRIGGER_OBJECT_MAP.LEASE_TRIGGERS[
              cta.text as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.LEASE_TRIGGERS
            ] || cta.text,
        });
        if (cta.text === 'View lease') {
          ManageTenantsCSEvents.ViewGeneratedLease({
            currentProperty: listing,
            user,
          });
        }
        break;
      default:
        break;
    }
  };

  const onEnableApplicationsClick = async () => {
    try {
      await setIsApplicationsAccepted({ getParams: { propertyId: listingAlias, isApplicationsAccepted: true } });
      await refetchInquiries();
      enqueueToast(
        <Toast tone="success">
          Applications are enabled for {fullAddress}. You can change application preferences anytime on the
          property&apos;s&nbsp;
          <em>Applications</em>
          &nbsp;tab.
        </Toast>,
      );
    } catch (error) {
      enqueueToast(<Toast tone="critical">There was an error enabling applications. Please try again.</Toast>);
    }
  };

  const getWrappedText = (value: string) => {
    let characterLimit = 30;
    characterLimit = isScreenXlGTE ? characterLimit : 23;
    const finalCharacterLimit = value.length > characterLimit ? characterLimit - 3 : characterLimit + 1;

    return stringHelper.wrapText(value, finalCharacterLimit);
  };

  const getPhotoContent = () => (
    <Box css={propertyPhotoContainerStyles}>
      <Box
        css={propertyPhotoRecipe.raw({ hasPhoto: !!thumb })}
        style={{ backgroundImage: `url(${thumb ? thumb : MediumIllustrationHouse2x})` }}
      />
    </Box>
  );

  const getCellText = ({ status, description, cta }: Partial<AccountViewTableCellDto>, label: string) => (
    <>
      {status &&
        (status.tagAppearance ? (
          <Tag
            size="sm"
            tone={status.tagAppearance === 'error' ? 'critical' : (status.tagAppearance as TagPropsInterface['tone'])}
            css={tagStyles}
          >
            {status.text.toUpperCase()}
          </Tag>
        ) : (
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            {status.url ? (
              <Anchor
                href={status.url}
                onClick={(event: React.MouseEvent<HTMLElement>) => event.stopPropagation()}
                css={cellAnchorStyles}
              >
                {status.text}
              </Anchor>
            ) : (
              <Text textStyle="body-sm" asChild css={{ textWrap: 'balance' }}>
                <p>{status.text}</p>
              </Text>
            )}
            {status.hasUnread && (
              <Icon
                render={<IconCircleFilled />}
                size="sm"
                css={{ marginLeft: '200', color: 'icon.feedback.notify.hero', width: '200' }}
              />
            )}
          </Box>
        ))}
      {description && (
        <Text textStyle="body-sm" css={{ color: status ? 'text.subtle' : 'text.neutral' }} asChild>
          <p>{description}</p>
        </Text>
      )}
      {cta && (
        <TextButton
          asChild={!!cta.url}
          onClick={(event: React.MouseEvent<HTMLElement>) => handleCellCtaClick(event, cta, label)}
          textStyle="body-sm-bold"
          data-eventid={cta.text === 'View lease' ? ManageTenantsCSEvents.ViewGeneratedLease.data_event_id : undefined}
        >
          {cta.url ? <a href={cta.url || ''}>{cta.text}</a> : cta.text}
        </TextButton>
      )}
    </>
  );

  const getCellDataEventId = (label: string) => {
    if (label === PROPERTY_TABLE_COLUMNS.RENT) {
      return PropertiesPageCSEvents.PropertyCardPayments.data_event_id;
    }
    if (label === PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS) {
      return PropertiesPageCSEvents.PropertyCardPotentialTenants.data_event_id;
    }
    if (label === PROPERTY_TABLE_COLUMNS.LEASES) {
      return PropertiesPageCSEvents.PropertyCardLeases.data_event_id;
    }
    return undefined;
  };

  const renderCellContent = (
    { status, description, cta, onlyDisplayOnHover }: Partial<AccountViewTableCellDto>,
    label: string,
  ) => {
    if (shouldHideColumnContent) {
      return <></>;
    }

    if (!status && !description && !cta) {
      return <>-</>;
    }

    if (onlyDisplayOnHover) {
      return (
        <>
          <Box
            data-eventdescription={cta?.text}
            data-eventid={getCellDataEventId(label)}
            css={{ display: 'inline', 'td:hover &': { display: 'none' } }}
          >
            -
          </Box>
          <Box css={{ display: 'none', 'td:hover &': { display: 'block' } }}>
            {getCellText({ status, description, cta }, label)}
          </Box>
        </>
      );
    }

    return getCellText({ status, description, cta }, label);
  };

  const renderCell = (
    { status, description, cta, onlyDisplayOnHover }: Partial<AccountViewTableCellDto> = {},
    label: string,
  ) => {
    return (
      <TableCell
        id={label === PROPERTY_TABLE_COLUMNS.LEASES ? 'PropertyRow-Lease-Cell' : undefined}
        data-eventdescription="Property card hover"
        data-eventid={PropertiesPageCSEvents.PropertyCardHover.data_event_id}
        aria-labelledby={label}
        onMouseOver={() => {
          if (listingAlias && onlyDisplayOnHover) {
            onUpsellCellHover(label, webAnalyticsData, lease);
          }
        }}
      >
        {renderCellContent({ status, description, cta, onlyDisplayOnHover }, label)}
      </TableCell>
    );
  };

  return (
    <>
      <Anchor href={targetListingRoute} onClick={handleRowClick} css={{ display: 'contents', textDecoration: 'none' }}>
        {/* we need to keep onClick handler or this will always reload the page  */}
        <Table.Row
          id={index != null ? `PropertyRow-${index}` : undefined}
          interactive={true}
          onClick={handleRowClick}
          data-testid="PropertyRow"
          data-eventid={PropertiesPageCSEvents.PropertyCardClick.data_event_id}
          data-eventdescription="Property card click"
          css={{ cursor: 'pointer', verticalAlign: 'middle' }}
        >
          <TableCell aria-labelledby={PROPERTY_TABLE_COLUMNS.ADDRESS}>
            <Box
              id={index != null ? `PropertyRow-${index}-Cell-0` : undefined}
              css={{ display: 'flex', flexDirection: 'row' }}
            >
              {isScreenLgGTE && getPhotoContent()}
              <Box css={flexContainerStyles}>
                <Box css={propertyAddressStyles}>
                  {isPostingPathAdmin ? (
                    <Text textStyle="body-sm-bold" asChild css={{ textWrap: 'wrap !important' }}>
                      <p>{propertyUtils.formatAddress(address).formattedCompleteAddress}</p>
                    </Text>
                  ) : (
                    <Text textStyle="body-sm-bold" asChild css={{ maxWidth: textMaxWidth }}>
                      <p>{getWrappedText(isMultifamily && name ? name : fullAddress)}</p>
                    </Text>
                  )}
                  <Text textStyle="body-sm" asChild css={{ maxWidth: textMaxWidth }}>
                    <p>
                      {listingStatusTagAppearance ? (
                        <Tag
                          size="sm"
                          tone={
                            listingStatusTagAppearance === 'error'
                              ? 'critical'
                              : (listingStatusTagAppearance as TagPropsInterface['tone'])
                          }
                          icon={
                            listingStatus === AccountViewListingStatus.UNABLE_TO_PUBLISH ? <IconWarningFilled /> : null
                          }
                          css={tagStyles}
                        >
                          {listingStatusText.toUpperCase()}
                        </Tag>
                      ) : (
                        <Text textStyle="body-sm" color="text.subtle">
                          {listingStatusText}
                        </Text>
                      )}
                    </p>
                  </Text>
                </Box>
              </Box>
            </Box>
          </TableCell>
          {renderCell(paymentsData, PROPERTY_TABLE_COLUMNS.RENT)}
          {renderCell(inquiriesData, PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS)}
          {renderCell(leasesData, PROPERTY_TABLE_COLUMNS.LEASES)}
          {!isScreenMobile && (
            <TableCell aria-labelledby={PROPERTY_TABLE_COLUMNS.ACTIONS}>
              <Box css={flexContainerStyles}>
                <div>
                  <Menu
                    open={showMoreListingAlias === listing.listingAlias}
                    onOpenChange={(open) => open && setShowMoreListingAlias(listing.listingAlias)}
                    content={panelsToRender.map(
                      (item) =>
                        item && (
                          <Menu.Item
                            key={item.id}
                            onClick={(event: React.MouseEvent<HTMLElement>) =>
                              handleMenuItemClick(item as PanelProps, event)
                            }
                          >
                            {item.title}
                          </Menu.Item>
                        ),
                    )}
                    trigger={
                      <IconButton
                        title="More actions"
                        icon={<IconMoreFilled />}
                        emphasis="bare"
                        css={{ color: 'icon.accent.blue.hero' }}
                        onClick={handleMenuClick}
                        data-eventid={PropertiesPageCSEvents.PropertyCardOverflowMenu.data_event_id}
                      />
                    }
                  />
                </div>
              </Box>
            </TableCell>
          )}
        </Table.Row>
      </Anchor>
      {displayApplicationsModal && (
        <EnableApplicationsModal
          setShowEnableApplicationsModal={setDisplayApplicationsModal}
          onConfirm={onEnableApplicationsClick}
        />
      )}
    </>
  );
};

export { PropertyRow };
export default PropertyRow;
